import { Card, Grid, CrudMode, IconButton, Tooltip, Typography, deepCopy, ValidationErrors } from "@compass/core";
import { Application, ApplicationEditResource, ApplicationQuestion, ApplicationSection, ApplicationViewResource } from "generated/openApi";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import produce from "immer";
import { useEffect, useState } from "react";
import { createEntity } from "utils/CreateEntity";
import { DragDropContext, Draggable } from "react-beautiful-dnd";
import { MyDroppable } from "components/common/Draggable/Droppable";
import { reorder } from "utils/Reorder";
import { ApplicationQuestionContainer } from "./ApplicationQuestionsContainer";

type ApplicationQuestionsProps = {
  application: Application;
  copyApplication: number | undefined;
  setApplication: (value: Application) => void;
  refreshApplication: Application | undefined;
  viewResource: ApplicationViewResource;
  editResource: ApplicationEditResource | undefined;
  crudMode: CrudMode;
  validationErrors: ValidationErrors;
  navigateEditMode: () => void;
};

export const ApplicationQuestions = ({
  application,
  copyApplication,
  setApplication,
  refreshApplication,
  viewResource,
  editResource,
  crudMode,
  validationErrors,
  navigateEditMode,
}: ApplicationQuestionsProps) => {
  const [questions, setQuestions] = useState<ApplicationQuestion[]>();
  const [selectedQuestion, setSelectedQuestion] = useState<ApplicationQuestion | null>();

  useEffect(() => {
    setQuestions(application.applicationSections?.flatMap((s) => s.applicationQuestions!) || []);
  }, [copyApplication]);

  useEffect(() => {
    setQuestions(application.applicationSections?.flatMap((s) => s.applicationQuestions!) || []);
  }, []);

  useEffect(() => {
    if (refreshApplication) {
      setQuestions(refreshApplication.applicationSections?.flatMap((s) => s.applicationQuestions!) || []);
    }
  }, [refreshApplication]);

  useEffect(() => {
    if (copyApplication) return;
    const temp = deepCopy(application.applicationSections);

    temp?.forEach((sec) => {
      sec.applicationQuestions = questions?.filter((q) => q.applicationSectionId == sec.id);
    });

    setApplication(
      produce(application, (draft) => {
        draft!.applicationSections = temp;
      })
    );
  }, [questions]);

  const addQuestion = (displayOrder: number, applicationSectionId: number) => {
    setQuestions(
      produce((draft) => {
        let newQuestion: ApplicationQuestion = createEntity<ApplicationQuestion>({
          applicationSectionId: applicationSectionId,
          displayOrder,
        });

        // increase the display order of all questions after the newly added question
        var increasingQuestions = draft?.filter((q) => q.applicationSectionId == applicationSectionId && q.displayOrder >= displayOrder);
        increasingQuestions?.forEach((q) => q.displayOrder++);

        draft?.push(newQuestion);

        draft?.sort((a, b) => a.displayOrder - b.displayOrder);

        setSelectedQuestion(newQuestion);
      })
    );
  };

  const updateQuestion = (section: ApplicationQuestion) => {
    setQuestions(
      produce((draft) => {
        let draftSectionIndex = draft?.findIndex((q) => q.id == section.id && q.displayOrder == section.displayOrder);

        draft![draftSectionIndex!] = section;
      })
    );
  };

  const grid = 8;

  const getItemStyle = (padding, isDragging, draggableStyle) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: "none",
    padding: grid * padding,
    margin: `0 0 ${grid}px 0`,

    // change background colour if dragging
    background: isDragging ? "lightgreen" : "white",

    // styles we need to apply on draggables
    ...draggableStyle,
  });

  const onDragEnd = (result) => {
    const { source, destination, type } = result;

    // dropped outside the list
    if (!destination) {
      return;
    }
    const sID = source.droppableId;
    const dID = destination.droppableId;

    if (sID === dID) {
      // source droppable is the same as the destination, so it is moving within the same container
      // just need to reorder the items in the container

      const items = reorder<ApplicationQuestion>(questions!, source.index, destination.index);

      setQuestions(items);
    }
  };

  const setSelected = (question: ApplicationQuestion | null) => {
    navigateEditMode();
    setSelectedQuestion(question);
  };

  const getBranchableQuestions = (question: ApplicationQuestion) : ApplicationQuestion[] => {
    if (!questions) return [];
    return questions?.filter(function (e) { return e.id !== question.id && e.tempId !== question.tempId; });
  }

  const getBranchableSections = () : ApplicationSection[] => {
    return application.applicationSections || [];
  }

  const renderQuestions = (question: ApplicationQuestion, index: number) => {
    return (
      <Draggable key={question.id} draggableId={question.id.toString()} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            style={getItemStyle(2, snapshot.isDragging, provided.draggableProps.style)}
          >
            <Grid container {...provided.dragHandleProps}>
              <ApplicationQuestionContainer
                viewResource={viewResource}
                application={application}
                question={question}
                updateQuestion={updateQuestion}
                addQuestion={addQuestion}
                selected={
                  selectedQuestion?.id == question.id && selectedQuestion?.displayOrder == question.displayOrder
                }
                setSelected={setSelected}
                key={`${question.id}:${question.displayOrder}`}
                validationErrors={validationErrors}
                branchableQuestions={getBranchableQuestions(question)}
                branchableSections={getBranchableSections()}
              />
            </Grid>
          </div>
        )}
      </Draggable>
    );
  };

  return (
    <Grid container spacing="lg">
      <DragDropContext onDragEnd={onDragEnd}>
        <Grid item xs={12}>
          <MyDroppable id="sections" type="sections">
            {application && questions?.filter((s) => !s.deleted).map((s, index) => renderQuestions(s, index))}
            {application && questions?.length == 0 && (
              <Grid item xs={10} display="flex" alignItems="center" justifyContent="center">
                {application!.applicationSections && application!.applicationSections.length > 0 && (
                  <Card>
                    <Typography>No questions yet, click to begin!</Typography>
                    <IconButton onClick={() => addQuestion(1, application!.applicationSections![0].id)}>
                      <Tooltip title="Add New Section">
                        <AddCircleOutlineIcon />
                      </Tooltip>
                    </IconButton>
                  </Card>
                )}
                {!application!.applicationSections || application!.applicationSections.length == 0 && (
                  <Card>
                    <Typography>No sections yet, Create one to add questions</Typography>
                  </Card>
                )}
              </Grid>
            )}
          </MyDroppable>
        </Grid>
      </DragDropContext>
    </Grid>
  );
};
